import { Link } from 'react-router-dom';
import { formatRelativeOrDate, Skeleton } from '@ah/design';
import { MediaThumb } from '@ah/design/mobile';
import type { NewsStoryListItem } from '../shared/index.ts';

export function NewsStoryRow({ story }: { story: NewsStoryListItem }) {
  return (
    <Link
      to={`/news/${story.id}`}
      className="flex gap-3 p-3 bg-card rounded-xl border border-border active:bg-accent"
    >
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <h2 className="text-sm font-semibold leading-snug line-clamp-2">{story.title}</h2>
        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-auto flex-wrap">
          {story.topics?.[0]?.name ? (
            <span className="text-[10px] uppercase tracking-wide rounded-full border px-1.5 py-0.5">
              {story.topics[0].name}
            </span>
          ) : null}
          <span>{story.articleCount} {story.articleCount === 1 ? 'source' : 'sources'}</span>
          {story.sourcePublishedAt ?? story.summaryGeneratedAt ? (
            <>
              <span>·</span>
              <span>{formatRelativeOrDate(story.sourcePublishedAt ?? story.summaryGeneratedAt!)}</span>
            </>
          ) : null}
        </div>
      </div>
      <MediaThumb
        src={story.headlineImageUrl}
        className="w-24 aspect-[16/10] rounded-lg shrink-0"
      />
    </Link>
  );
}

export function ListSkeleton({ variant }: { variant: 'story' | 'video' | 'episode' | 'grid' }) {
  const count = variant === 'grid' ? 6 : 5;
  if (variant === 'grid') {
    return (
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="aspect-square rounded-xl" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </div>
    );
  }
  if (variant === 'video') {
    return (
      <div className="space-y-3">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="aspect-video rounded-xl" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        ))}
      </div>
    );
  }
  if (variant === 'episode') {
    return (
      <div className="space-y-3">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="flex gap-3 p-3 bg-card rounded-xl border border-border">
            <Skeleton className="w-14 h-14 rounded-lg shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex gap-3 p-3 bg-card rounded-xl border border-border">
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="w-24 aspect-[16/10] rounded-lg shrink-0" />
        </div>
      ))}
    </div>
  );
}

export function ErrorRetry({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <div className="text-center py-16">
      <p className="text-sm text-destructive mb-3">{label}</p>
      <button
        type="button"
        onClick={onRetry}
        className="text-sm px-3 py-1.5 rounded-full border border-border bg-card hover:bg-accent"
      >
        Retry
      </button>
    </div>
  );
}
